<div class="bg-dark-grey min-vh-100 d-flex flex-row align-items-center">
  <c-container breakpoint="md">
    <c-row class="justify-content-center">
      <c-col lg="8" xl="6">
        <c-card-group>
          <c-card class="p-4 shadow-sm border-0">
            <c-card-body class="text-center">
              <!-- Logo -->
              <app-logo class="mb-4 d-block mx-auto" style="max-width: 180px;"></app-logo>

              <!-- Validating State -->
              @if (validating()) {
                <div class="my-5">
                  <c-spinner color="primary" variant="grow" class="mb-4"></c-spinner>
                  <h4 class="mb-3">{{ 'confirm_email.validating' | translate }}</h4>
                  <p class="text-body-secondary">
                    {{ 'confirm_email.validating_message' | translate }}
                  </p>
                </div>
              }

              <!-- Success State -->
              @if (success()) {
                <div class="my-5">
                  <div class="display-4 text-success mb-4">
                    <svg cIcon name="cilXCircle"></svg>
                  </div>
                  <h2 class="text-success mb-3">{{ 'confirm_email.setup_success_title' | translate }}</h2>
                  <p class="lead mb-4">
                    {{ 'confirm_email.setup_success_message' | translate }}
                  </p>
                  <p class="text-body-secondary mb-4">
                    {{ 'confirm_email.redirecting' | translate }}...
                  </p>
                  <c-spinner color="success" class="mt-3"></c-spinner>
                </div>
              }

              <!-- Error State (invalid token) -->
              @if (!tokenValid() && error(); as err) {
                <div class="my-5">
                  <div class="display-4 text-danger mb-4">
                    <svg cIcon name="cilXCircle"></svg>
                  </div>
                  <h2 class="text-danger mb-3">{{ 'confirm_email.setup_error_title' | translate }}</h2>
                  <div class="alert alert-danger mb-4">
                    {{ err }}
                  </div>
                  <p class="text-body-secondary mb-4">
                    {{ 'confirm_email.setup_error_support' | translate }}
                  </p>
                  <div class="d-flex justify-content-center gap-3">
                    <button cButton color="primary" routerLink="/login">
                      {{ 'confirm_email.go_to_login' | translate }}
                    </button>
                    <button cButton color="link" routerLink="/forgot-password">
                      {{ 'confirm_email.request_new_link' | translate }}
                    </button>
                  </div>
                </div>
              }

              <!-- Password Setup Form -->
              @if (tokenValid() && !success()) {
                <form cForm [formGroup]="passwordForm" (ngSubmit)="onSubmit()">
                  <h2 class="mb-1">{{ 'confirm_email.setup_password_title' | translate }}</h2>
                  <p class="text-body-secondary mb-4">
                    {{ 'confirm_email.setup_password_welcome' | translate }}{{ firstName() ? ', ' + firstName() : '' }}!
                  </p>

                  <!-- Email Display -->
                  @if (email()) {
                    <div class="mb-3">
                      <label cLabel class="fw-semibold">{{ 'confirm_email.email_label' | translate }}</label>
                      <p class="text-body-secondary mb-0">{{ email() }}</p>
                    </div>
                  }

                  <!-- Password Field -->
                  <c-input-group class="mb-3">
                    <span cInputGroupText>
                      <svg cIcon name="cilLockLocked"></svg>
                    </span>
                    <input
                      cFormControl
                      [type]="passwordVisible() ? 'text' : 'password'"
                      formControlName="password"
                      placeholder="{{ 'confirm_email.password_placeholder' | translate }}"
                      [class.is-valid]="password.touched && password.valid"
                      [class.is-invalid]="password.touched && password.invalid"
                    />
                    <span cInputGroupText (click)="togglePasswordVisibility()" style="cursor: pointer;">
                      <svg cIcon [name]="passwordVisible() ? 'cilEyeSlash' : 'cilEye'"></svg>
                    </span>

                    @if (password.touched && password.invalid) {
                      <c-form-feedback [valid]="false">
                        @if (password.errors?.['required']) {
                          <div>{{ 'confirm_email.password_required' | translate }}</div>
                        }
                        @if (password.errors?.['minlength']) {
                          <div>{{ 'confirm_email.password_minlength' | translate }}</div>
                        }
                      </c-form-feedback>
                    }
                  </c-input-group>

                  <!-- Confirm Password Field -->
                  <c-input-group class="mb-4">
                    <span cInputGroupText>
                      <svg cIcon name="cilLockLocked"></svg>
                    </span>
                    <input
                      cFormControl
                      [type]="confirmPasswordVisible() ? 'text' : 'password'"
                      formControlName="confirmPassword"
                      placeholder="{{ 'confirm_email.confirm_password_placeholder' | translate }}"
                      [class.is-valid]="confirmPassword.touched && confirmPassword.valid"
                      [class.is-invalid]="confirmPassword.touched && confirmPassword.invalid"
                    />
                    <span cInputGroupText (click)="toggleConfirmPasswordVisibility()" style="cursor: pointer;">
                      <svg cIcon [name]="confirmPasswordVisible() ? 'cilEyeSlash' : 'cilEye'"></svg>
                    </span>

                    @if (confirmPassword.touched && confirmPassword.invalid) {
                      <c-form-feedback [valid]="false">
                        @if (confirmPassword.errors?.['required']) {
                          <div>{{ 'confirm_email.confirm_required' | translate }}</div>
                        }
                        @if (confirmPassword.errors?.['passwordMismatch']) {
                          <div>{{ 'confirm_email.passwords_mismatch' | translate }}</div>
                        }
                      </c-form-feedback>
                    }
                  </c-input-group>

                  <!-- General Error -->
                  @if (error(); as err) {
                    <div class="alert alert-danger mb-4">
                      {{ err }}
                    </div>
                  }

                  <!-- Submit Button -->
                  <c-row>
                    <c-col xs="12">
                      <button
                        cButton
                        color="primary"
                        type="submit"
                        class="w-100"
                        [disabled]="!canSubmit()"
                      >
                        @if (loading()) {
                          <c-spinner size="sm" class="me-2"></c-spinner>
                          {{ 'confirm_email.processing' | translate }}
                        } @else {
                          {{ 'confirm_email.complete_registration' | translate }}
                        }
                      </button>
                    </c-col>
                  </c-row>

                  <!-- Help Links -->
                  <div class="text-center mt-3">
                    <small class="text-body-secondary">
                      {{ 'confirm_email.already_have_password' | translate }}
                      <a routerLink="/login" class="text-decoration-none">
                        {{ 'confirm_email.sign_in_here' | translate }}
                      </a>
                    </small>
                  </div>
                </form>
              }
            </c-card-body>
          </c-card>
        </c-card-group>
      </c-col>
    </c-row>
  </c-container>
</div>